/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Handles session management and profile loading.
 */

import { createContext, useContext, useEffect, useState } from 'react'
import { supabase, normalizeError } from '@/lib/supabaseClient'

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  error: null,
})

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load profile for authenticated user
  async function loadProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) throw error

      if (!data) {
        throw new Error('Profile not found. Please contact support.')
      }

      if (data.is_disabled) {
        await supabase.auth.signOut()
        setUser(null)
        setProfile(null)
        throw new Error('Your account has been disabled. Please contact support.')
      }

      setProfile(data)
      return { data, error: null }
    } catch (err) {
      const normalizedError = normalizeError(err)
      setError(normalizedError)
      return { data: null, error: normalizedError }
    }
  }

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        loadProfile(session.user.id).finally(() => setLoading(false))
      } else {
        setLoading(false)
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)

      if (session?.user) {
        (async () => {
          await loadProfile(session.user.id)
          setLoading(false)
        })()
      } else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Sign in with email and password
  async function signIn(email, password) {
    try {
      setError(null)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error
      return { data, error: null }
    } catch (err) {
      const normalizedError = normalizeError(err)
      setError(normalizedError)
      return { data: null, error: normalizedError }
    }
  }

  // Sign up with email and password
  async function signUp(email, password, metadata = {}) {
    try {
      setError(null)

      // Sign up user - profile is created automatically by database trigger
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      })

      if (authError) throw authError
      if (!authData.user) throw new Error('Sign up failed')

      if (metadata.phone || metadata.role || metadata.buyer_category) {
        await new Promise(resolve => setTimeout(resolve, 100))

        const updateData = {
          phone: metadata.phone || null,
          role: metadata.role || 'buyer',
        }

        if (metadata.buyer_category) {
          updateData.buyer_category = metadata.buyer_category
        }

        const { error: updateError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', authData.user.id)

        if (updateError) console.warn('Failed to update profile:', updateError)
      }

      return { data: authData, error: null }
    } catch (err) {
      const normalizedError = normalizeError(err)
      setError(normalizedError)
      return { data: null, error: normalizedError }
    }
  }

  // Sign out
  async function signOut() {
    try {
      setError(null)
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setUser(null)
      setProfile(null)
      return { error: null }
    } catch (err) {
      const normalizedError = normalizeError(err)
      setError(normalizedError)
      return { error: normalizedError }
    }
  }

  const value = {
    user,
    profile,
    loading,
    error,
    signIn,
    signUp,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
